import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface CartItem {
  productId: string
  /** Null for a product that sells as a single SKU (no variants). */
  variantId: string | null
  sku: string
  name: string
  quantity: number
}

/** A cart line is identified by product *and* variant, so two sizes of one
 *  product stay separate lines. */
export const lineKey = (item: Pick<CartItem, 'productId' | 'variantId'>): string =>
  `${item.productId}:${item.variantId ?? ''}`

interface CartState {
  items: CartItem[]
  addItem: (item: Omit<CartItem, 'quantity'>) => void
  removeItem: (key: string) => void
  updateQuantity: (key: string, quantity: number) => void
  clearCart: () => void
  cartCount: () => number
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],

      addItem: (item) =>
        set((state) => {
          const key = lineKey(item)
          const existing = state.items.find((i) => lineKey(i) === key)
          if (existing) {
            return {
              items: state.items.map((i) =>
                lineKey(i) === key ? { ...i, quantity: i.quantity + 1 } : i,
              ),
            }
          }
          return { items: [...state.items, { ...item, quantity: 1 }] }
        }),

      removeItem: (key) =>
        set((state) => ({
          items: state.items.filter((i) => lineKey(i) !== key),
        })),

      updateQuantity: (key, quantity) =>
        set((state) => ({
          items:
            quantity <= 0
              ? state.items.filter((i) => lineKey(i) !== key)
              : state.items.map((i) => (lineKey(i) === key ? { ...i, quantity } : i)),
        })),

      clearCart: () => set({ items: [] }),

      cartCount: () => get().items.reduce((sum, i) => sum + i.quantity, 0),
    }),
    { name: 'helin-webshop-cart' },
  ),
)
